import GlassCard from '@/components/ui/glass-card';
import { Input } from '@/components/ui/input';
import { useAppointments } from '@/hooks/use-appointments';
import { AppointmentsStateSelector } from '@/store/selectors';
import { type SimplifiedAppointment } from '@/types/appointment';
import { Calendar, Clock, RefreshCw, Search, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface TodaysScheduleProps {
  onAppointmentSelect?: (appointment: SimplifiedAppointment) => void;
}

const TodaysSchedule = ({ onAppointmentSelect }: TodaysScheduleProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const dispatch = useDispatch();
  const { appointments, refetch } = useAppointments();
  const loading = useSelector(AppointmentsStateSelector.loading);
  const selectedAppointment = useSelector(
    AppointmentsStateSelector.selectedAppointment
  );

  const formatTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const handleAppointmentSelect = (appointment: SimplifiedAppointment) => {
    try {
      onAppointmentSelect?.(appointment);
    } catch (error) {
      console.error('Error selecting appointment:', error);
    }
  };

  const filteredAppointments =
    appointments
      ?.filter((appointment) => {
        const patientName = appointment.patientName.toLowerCase();
        const patientFirstName =
          appointment.patientFirstName?.toLowerCase() || '';
        const patientLastName =
          appointment.patientLastName?.toLowerCase() || '';
        const serviceType = appointment.service.toLowerCase();
        const searchLower = searchQuery.toLowerCase();
        return (
          patientName.includes(searchLower) ||
          patientFirstName.includes(searchLower) ||
          patientLastName.includes(searchLower) ||
          serviceType.includes(searchLower) ||
          appointment.status.toLowerCase().includes(searchLower)
        );
      })
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      ) || [];

  return (
    <div className="flex flex-col h-full">
      {/* Status Bar */}
      <div className="flex items-center justify-between pb-1 gap-2 px-2">
        <div className="gap-2 w-full">
          {/* Search Input */}
          <div className="relative group mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 h-4 w-4 z-10" />
            <Input
              type="text"
              placeholder="Search appointments..."
              className="pl-8 rounded-3xl bg-white/60 dark:bg-slate-700/60 backdrop-blur-md border border-gray-400/40 dark:border-slate-600/40 shadow-sm focus-visible:ring-2 focus-visible:ring-eyevantage-primary/30 focus-visible:border-eyevantage-primary/50 placeholder:text-slate-500/80 dark:placeholder:text-slate-400/80 text-md font-medium transition-all duration-300"
              style={{
                height: '2.2rem',
              }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        <button
          onClick={() => refetch()}
          style={{
            marginTop: '-5px',
          }}
          disabled={loading}
          className="group p-2 relative hover:bg-white/40 dark:hover:bg-slate-700/40 rounded-3xl border border-gray-400/40 dark:border-slate-600/40 transition-all duration-300 dark:hover:border-slate-600/30"
          title="Refresh appointments">
          <RefreshCw
            className={`h-4 w-4 text-slate-600 dark:text-slate-400 group-hover:text-eyevantage-primary dark:group-hover:text-eyevantage-secondary transition-colors ${
              loading ? 'animate-spin' : ''
            }`}
          />
        </button>
      </div>

      {/* Scrollable Content */}
      <div
        className="flex-1 overflow-y-auto py-2 px-1 space-y-3"
        style={{
          maxHeight: 'calc(100vh - 17.6rem)',
          scrollbarWidth: 'none',
          minHeight: 'calc(100vh - 17.6rem)',
        }}>
        {loading ? (
          <div
            className="flex flex-col items-center justify-center text-slate-500 dark:text-slate-400"
            style={{ height: 'calc(100vh - 19rem)' }}>
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-eyevantage-primary border-t-transparent mb-3"></div>
            <span className="text-xl font-light mb-3 text-slate-500 dark:text-slate-300">
              Loading appointments...
            </span>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-slate-500 dark:text-slate-400 text-center"
            style={{ height: 'calc(100vh - 19rem)' }}>
            <Calendar className="h-20 w-20 opacity-50 mb-4" />
            <h4 className="text-xl font-light mb-3 text-slate-500 dark:text-slate-300">
              No appointments found
            </h4>
            <p className="text-md opacity-70">
              {searchQuery ? 'Try adjusting your search' : 'for today'}
            </p>
          </div>
        ) : (
          filteredAppointments.map((appointment) => {
            const isSelected =
              selectedAppointment?.appointmentId === appointment.appointmentId;

            // Use new patient data if available, fallback to display name
            const displayName =
              appointment.patientFirstName && appointment.patientLastName
                ? `${appointment.patientFirstName} ${appointment.patientLastName}`
                : appointment.patientName;

            return (
              <GlassCard
                key={appointment.appointmentId}
                variant="default"
                className={`cursor-pointer transition-all border border-gray-400/20 dark:border-slate-700/50 duration-300 relative ${
                  isSelected
                    ? 'ring-2 ring-purple-500/80 dark:ring-purple-500/80 shadow-lg shadow-purple-500/25 dark:shadow-purple-500/20'
                    : 'hover:shadow-xl'
                }`}
                onClick={() => handleAppointmentSelect(appointment)}>
                {/* Elegant selection indicators */}
                {isSelected && (
                  <>
                    {/* Subtle glow overlay for selection */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 dark:from-purple-500/10 dark:to-blue-500/10 rounded-xl pointer-events-none" />
                  </>
                )}

                {/* Content container - no shifting */}
                <div className="relative z-10">
                  {/* Header - Patient and Time */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {/* Avatar */}
                      <div className="w-8 h-8 rounded-3xl flex items-center justify-center text-md text-white bg-gradient-to-t from-slate-600 to-slate-800 dark:from-slate-700 dark:via-slate-600 dark:to-slate-600 border border-slate-300/50 dark:border-slate-500/50 transition-all duration-300">
                        {appointment.patientFirstName.charAt(0).toUpperCase()}
                        {appointment.patientLastName.charAt(0).toUpperCase()}
                      </div>

                      <div>
                        <h3 className="text-lg text-slate-800 dark:text-slate-200 transition-colors duration-300">
                          {displayName}
                        </h3>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4 text-slate-500 dark:text-slate-400 transition-colors duration-300" />
                      <span className="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors duration-300">
                        {formatTime(appointment.startTime)}
                      </span>
                    </div>
                  </div>

                  {/* Service and Status */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-md font-medium text-slate-700 dark:text-slate-400 transition-colors duration-300">
                      {appointment.service}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mb-1">
                    {/* Patient Details */}
                    {(appointment.patientBirthDate ||
                      appointment.patientGender) && (
                      <div className="flex items-center gap-2">
                        <UserRound className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400 transition-colors duration-300" />
                        <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 transition-colors duration-300">
                          {appointment.patientBirthDate && (
                            <span>
                              DOB: {formatDate(appointment.patientBirthDate)}
                            </span>
                          )}
                          {appointment.patientBirthDate &&
                            appointment.patientGender && (
                              <span className="w-1 h-1 bg-current rounded-full opacity-50" />
                            )}
                          {appointment.patientGender && (
                            <span>{appointment.patientGender}</span>
                          )}
                        </div>
                      </div>
                    )}
                    {appointment.status && (
                      <div
                        className={`text-sm border border-slate-300/50 dark:border-slate-500/50 rounded-md px-2 py-1 transition-all duration-300 ${
                          appointment.status.toLowerCase() === 'pending'
                            ? 'text-orange-700 border-orange-500'
                            : appointment.status.toLowerCase() === 'completed'
                            ? 'text-green-700 border-green-500'
                            : appointment.status.toLowerCase() === 'recording'
                            ? 'text-orange-700 dark:text-orange-300 border-orange-500'
                            : 'text-blue-700 dark:text-blue-300 border-blue-500'
                        }`}>
                        {appointment.status.toLowerCase()}
                      </div>
                    )}
                  </div>
                </div>
              </GlassCard>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TodaysSchedule;
